import React, { useEffect, useState, useContext } from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { App, Button, Switch, Table, Tag, Space, Tooltip } from 'antd'
import {
  CloseOutlined,
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
  LoadingOutlined,
  WarningOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort'
import arrayMove from 'array-move'
import sortBy from 'lodash/sortBy'
import differenceWith from 'lodash/differenceWith'
import isEqual from 'lodash/isEqual'
import * as Sentry from '@sentry/react'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { getLeaveTypesListAndLocations } from '../../../graphql/custom-queries'
import * as logger from '../../../services/logger'

import { useAppSelector } from '../../../store/hooks'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { notificationStore } from '../../../context/notificationsContext/store'
import { invertHexColor } from '../../../util/invert-color-wrapper'

import IntlMessages from '../../../util/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import CreateLeaveTypeForm from '../../../components/create-leave-type-form'
import EditLeaveTypeForm from '../../../components/edit-leave-type-form'

import { ColumnsType } from 'antd/lib/table'
import { IGetLeaveTypesListAndLocationsData, ILeaveTypeUpdates, ILeaveTypeWithAssignLocations } from '../../../types/leave-types'
import { IGetLeaveTypeList } from '../../../types/custom-queries'
import { IGetLocationsShort } from '../../../types/locations'
import { ISimilarLeaveType } from '@vacationtracker/shared/types/leave-type'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { useLocationAdmin } from '../../../hooks/use-location-admin'
import NoPermissionTooltip from '../../../components/no-permission-tooltip'
import { clearWizardState } from '../../../util/onboarding-wizard-storage'

const LeaveTypesPage = () => {
  const { formatMessage } = useIntl()
  const { message, modal, notification } = App.useApp()
  const {
    authUser: { id, platform },
  } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { isLocationAdmin, managedLocationIds } = useLocationAdmin()

  const { actionNotifications, setActionNotifications, eventsNotifications } = useContext(notificationStore)
  const [loading, setLoading] = useState(true)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [isCreateLeaveTypeAndAssignPolicyWizardVisible, setIsCreateLeaveTypeAndAssignPolicyWizardVisible] = useState(false)
  const [isUpdateLeaveTypeFormVisible, setUpdateLeaveTypeFormVisibility] = useState(false)
  const [leaveTypesList, setLeaveTypesList] = useState<ILeaveTypeWithAssignLocations[]>([])
  const [locations, setLocations] = useState<IGetLocationsShort[]>([])
  const [selectedLeaveType, setSelectedLeaveType] = useState<ILeaveTypeWithAssignLocations>()
  const [assigingPolicyCorrelationId, setAssigningPolicyCorrelationId] = useState('')
  const [assigningPolicyCorrelationExist, setAssigningPolicyCorrelationExist] = useState(false)
  const [shouldReloadOnActionNotificationsChange, setShouldReloadOnActionNotificationsChange] = useState(true)
  const [notAssignedLocations, setNotAssignedLocations] = useState<IGetLocationsShort[]>([])
  const [isAssigningLeaveTypeToLocationModalVisible, setIsAssigningLeaveTypeToLocationModalVisible] = useState(false)
  const [selectedLeaveTypeName, setSelectedLeaveTypeName] = useState('')
  const [leaveTypeId, setLeaveTypeId] = useState<string>('')
  const [similarLeaveType, setSimilarLeaveType] = useState<ISimilarLeaveType | null>(null)
  const [isCheckForLeaveTypeSimilarityLoading, setIsCheckForLeaveTypeSimilarityLoading] = useState(false)

  const [getLeaveTypesListAndLocationsQuery] = useManualQuery<IGetLeaveTypesListAndLocationsData>(getLeaveTypesListAndLocations)

  useEffect(() => {
    fetchLeaveTypesList()
  }, [])

  useEffect(() => {
    // we should reload HERE only when we just add leave type and do not assign policy to any location
    if (shouldReloadOnActionNotificationsChange) {
      fetchLeaveTypesList()
    }
  }, [actionNotifications])

  useEffect(() => {
    // reload when all leave policies are assigned thorugh wizard
    if (eventsNotifications && eventsNotifications[assigingPolicyCorrelationId]) {
      setAssigningPolicyCorrelationExist(true)
    }
    if (eventsNotifications && !eventsNotifications[assigingPolicyCorrelationId] && assigningPolicyCorrelationExist) {
      setAssigningPolicyCorrelationExist(false)
      setAssigningPolicyCorrelationId('')
      fetchLeaveTypesList()
    }
  }, [eventsNotifications])

  const DragableBodyRow = ({ ...restProps }) => {
    // Wrap tr in a div to maintain structure when dragged
    return (
      <SortableItem>
        <tr {...restProps} />
      </SortableItem>
    )
  }

  const createNewLeaveType = () => {
    setSelectedLeaveType(undefined)
    setIsCreateLeaveTypeAndAssignPolicyWizardVisible(true)
  }

  const handleErrorNotification = (error, correlationId?: string, title?: string): void => {
    const description = correlationId
      ? formatMessage({ id: 'error.notificationGeneral' }, { correlationId })
      : error.response?.data?.message
        ? error.response?.data?.message
        : error.message
          ? error.message
          : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'error.notificationGeneral' }),
      description,
      duration: 0,
    })
  }

  const updateLeaveTypesOrder = async (types: ILeaveTypeWithAssignLocations[]) => {
    try {
      setLoading(true)
      await Api.post('/core/event', {
        eventType: 'LEAVE_TYPE_ORDER',
        eventGroup: 'COMPANY',
        positions: types.map((type) => {
          return {
            leaveTypeId: type.id,
            position: type.position,
          }
        }),
      })

      setLoading(false)
    } catch (error) {
      logger.warning('error update leave type order', error)
      setLoading(false)
      message.error(error.message)
    }
  }

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    if (oldIndex !== newIndex) {
      const newLeaveTypesPosition = arrayMove(leaveTypesList, oldIndex, newIndex)
      const leaveTypeListSorted = newLeaveTypesPosition.map((lt: ILeaveTypeWithAssignLocations, index: number) => {
        return {
          ...lt,
          position: index,
          index,
          key: index,
          id: lt.id,
          name: lt.name,
        }
      })
      updateLeaveTypesOrder(leaveTypeListSorted)
      setLeaveTypesList(leaveTypeListSorted)
    }
  }

  const fetchLeaveTypesList = async () => {
    try {
      const response = await getLeaveTypesListAndLocationsQuery()
      if (!response.data || response.error) throw response.error
      setLocations(response.data.getLocationList)
      const leaveTypes: IGetLeaveTypeList[] = response.data.getLeaveTypeList
        ? response.data.getLeaveTypeList.sort((a: IGetLeaveTypeList, b: IGetLeaveTypeList) => (a.position < b.position ? -1 : 1))
        : []
      setLeaveTypesList(
        leaveTypes.map((lt: IGetLeaveTypeList, index: number) => {
          return {
            index,
            key: index,
            assignedToLocations: lt.leavePolicies.length > 0 ? lt.leavePolicies.map((leaveType) => leaveType.location) : [],
            ...lt,
          }
        })
      )
      setLoading(false)
    } catch (err) {
      logger.warning('error fetching leave types list', err)
    }
  }

  const showConfirmDelete = (leaveTypeId: string, name: string) => {
    modal.confirm({
      title: formatMessage({ id: 'leaveTypes.deleteLeaveType' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'leaveTypes.deleteLeaveTypeConfirm' }, { name }),
      okText: formatMessage({ id: 'leaveTypes.deleteLeaveType' }),
      okType: 'danger',
      maskClosable: true,
      onOk() {
        deleteLeaveType(leaveTypeId, name)
      },
    })
  }

  const deleteLeaveType = async (leaveTypeId, name) => {
    try {
      setLoading(true)
      const response = await Api.post('/core/event', {
        eventType: 'LEAVE_TYPE_DELETED',
        eventGroup: 'LEAVE_TYPE',
        leaveTypeId,
      })

      clearWizardState(id, authCompany?.id)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'leaveTypes.deleteInProgress' }, { leaveTypeName: name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
      setShouldReloadOnActionNotificationsChange(true)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      message.error(error)
    }
  }

  const handleMenuClick = (target, leaveTypeId, name) => {
    if (target === 'deleteLeaveType') {
      showConfirmDelete(leaveTypeId, name)
    } else if (target === 'editLeaveType') {
      const selected = leaveTypesList.find((type) => type.id === leaveTypeId)
      if (!selected) {
        return
      }
      setSelectedLeaveType(selected)
      setUpdateLeaveTypeFormVisibility(true)
    }
  }

  const DragHandle = () => {
    if (isLocationAdmin) {
      return (
        <NoPermissionTooltip>
          <MenuOutlined style={{ cursor: 'not-allowed', color: '#999', opacity: 0.5 }} />
        </NoPermissionTooltip>
      )
    }
    return (
      <SortableKnob>
        <MenuOutlined
          style={{
            cursor: 'grab',
            color: '#999',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        />
      </SortableKnob>
    )
  }

  const DraggableContainer = (props: any) => {
    return (
      <SortableList onSortEnd={onSortEnd} className="sortable-list" draggedItemClassName="dragged-table-item" allowDrag={!isLocationAdmin}>
        <tbody {...props} />
      </SortableList>
    )
  }

  const onUpdateLeaveType = async (leaveType: Partial<ILeaveTypeUpdates>) => {
    try {
      setUpdateLoading(true)
      setIsCreateLeaveTypeAndAssignPolicyWizardVisible(false)
      setLoading(true)
      const response = await Api.post('/core/event', {
        eventType: 'LEAVE_TYPE_UPDATED',
        eventGroup: 'LEAVE_TYPE',
        ...leaveType,
      })
      setUpdateLeaveTypeFormVisibility(false)

      clearWizardState(id, authCompany?.id)
      notification.open({
        key: response.correlationId,
        message: leaveType.leaveTypeId
          ? formatMessage({ id: 'leaveTypes.updateInProgress' }, { leaveTypeName: response.name })
          : formatMessage({ id: 'leaveTypes.createInProgress' }, { leaveTypeName: response.name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
      setShouldReloadOnActionNotificationsChange(true)
      setLoading(false)
      setUpdateLoading(false)
      setSimilarLeaveType(null)
    } catch (error) {
      setLoading(false)
      setUpdateLoading(false)
      handleErrorNotification(error)
    }
  }

  const confirmChangeStatus = (levaeType) => {
    modal.confirm({
      title: levaeType.isActive ? formatMessage({ id: 'leaveTypes.deactivateLeaveType' }) : formatMessage({ id: 'leaveTypes.activatingLeaveType' }),
      icon: <ExclamationCircleOutlined />,
      content: levaeType.isActive ? formatMessage({ id: 'leaveTypes.deactivateLeaveTypeInfo' }) : formatMessage({ id: 'leaveTypes.activatingLeaveTypeInfo' }),
      okText: formatMessage({ id: 'app.yes' }),
      cancelText: formatMessage({ id: 'app.no' }),
      onOk() {
        onUpdateLeaveType({
          leaveTypeId: levaeType.id,
          name: levaeType.name,
          isActive: !levaeType.isActive,
          color: levaeType.color,
        })
      },
    })
  }

  const leaveTypesColumns: ColumnsType<ILeaveTypeWithAssignLocations> = [
    {
      title: <IntlMessages id="leaveTypes.sort" />,
      dataIndex: 'sort',
      width: 30,
      className: 'drag-visible',
      // eslint-disable-next-line react/display-name
      render: () => <DragHandle />,
    },
    {
      title: <IntlMessages id="app.active" />,
      dataIndex: 'isActive',
      key: 'isActive',
      className: 'drag-visible',
      render: (isActive: boolean, row) => {
        const hasOtherActiveLeaveType = leaveTypesList.some((lt) => lt.isActive && lt.id !== row.id)
        const isFreemiumDisabled = authCompany?.plan === SubscriptionPlanEnum.freemium && !isActive && hasOtherActiveLeaveType

        if (isLocationAdmin) {
          return (
            <NoPermissionTooltip>
              <Switch disabled checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} checked={isActive} />
            </NoPermissionTooltip>
          )
        }

        return (
          <Switch
            disabled={isFreemiumDisabled}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={isActive}
            onChange={() => confirmChangeStatus(row)}
          />
        )
      },
    },
    {
      title: <IntlMessages id="app.name" />,
      dataIndex: 'name',
      key: 'name',
      className: 'drag-visible leave-type-name',
    },
    {
      title: <IntlMessages id="leaveTypes.assignedToLocationsColumn" />,
      dataIndex: 'assignedToLocations',
      key: 'assignedToLocations',
      className: 'drag-visible',
      render: (ltLocations, leaveType) => {
        const allLocationsUnsorted = locations.filter(Boolean)
        const leaveTypelocationsUnsorted = ltLocations.filter(Boolean)
        const locationsSorted = sortBy(leaveTypelocationsUnsorted, [(location) => location.name.toUpperCase()])
        const allNotAssigned = differenceWith(allLocationsUnsorted, leaveTypelocationsUnsorted, isEqual)
        const notAssignedLocations = isLocationAdmin ? allNotAssigned.filter((loc) => managedLocationIds.includes(loc.id)) : allNotAssigned
        const onAddClickHandler = () => {
          setLeaveTypeId(leaveType.id)
          setSelectedLeaveTypeName(leaveType.name)
          setNotAssignedLocations(notAssignedLocations)
          setIsAssigningLeaveTypeToLocationModalVisible(true)
        }
        const isLinkDisabled = authCompany?.plan === SubscriptionPlanEnum.freemium && !leaveType.isActive
        return (
          <>
            {locationsSorted.length > 0 ? (
              locationsSorted.map((loc) => {
                const isManagedLocation = !isLocationAdmin || managedLocationIds.includes(loc.id)
                return (
                  <Tag key={loc.id}>
                    {isLinkDisabled || !isManagedLocation ? (
                      <span style={{ cursor: 'not-allowed', opacity: 0.5 }}>{loc.name}</span>
                    ) : (
                      <Link to={`/app/settings/location/${loc.id}/leave-policies/${leaveType.id}`}>{loc.name}</Link>
                    )}
                  </Tag>
                )
              })
            ) : (
              <>
                <WarningOutlined style={{ color: 'red' }} />
                <span style={{ color: 'red', marginRight: 5 }}>
                  <IntlMessages id="leaveTypes.assignedToLocationsColumnWarning" />
                </span>
              </>
            )}
            {notAssignedLocations.length > 0 && (
              <Tag style={{ cursor: 'pointer' }} onClick={onAddClickHandler} className="hover-show-text">
                <PlusOutlined />
                <span className="hover-text">
                  <IntlMessages id="components.createLeaveTypeForm.assignAndConfigure" />
                </span>
              </Tag>
            )}
          </>
        )
      },
    },
    {
      title: <IntlMessages id="app.color" />,
      dataIndex: 'color',
      className: 'drag-visible color',
      key: 'color',
      render: (color: string) => {
        return <span style={{ background: color, color: invertHexColor(color, true) }}>{color}</span>
      },
    },
    {
      title: '',
      width: 90,
      dataIndex: 'id',
      key: 'id',
      render: (id: string, row) => {
        const isFreemiumDisabledEdit = authCompany?.plan === SubscriptionPlanEnum.freemium && !row.isActive
        const isFreemiumDisabledDelete = authCompany?.plan === SubscriptionPlanEnum.freemium

        return (
          <Space size="middle">
            {isLocationAdmin ? (
              <NoPermissionTooltip>
                <EditOutlined style={{ cursor: 'not-allowed', opacity: 0.5 }} />
              </NoPermissionTooltip>
            ) : (
              <Tooltip title={<IntlMessages id="leaveTypes.editLeaveType" />}>
                <EditOutlined
                  style={{
                    cursor: isFreemiumDisabledEdit ? 'not-allowed' : 'pointer',
                    opacity: isFreemiumDisabledEdit ? 0.5 : 1,
                  }}
                  onClick={() => (isFreemiumDisabledEdit ? null : handleMenuClick('editLeaveType', id, row.name))}
                />
              </Tooltip>
            )}
            {isLocationAdmin ? (
              <NoPermissionTooltip>
                <DeleteOutlined style={{ cursor: 'not-allowed', opacity: 0.5 }} />
              </NoPermissionTooltip>
            ) : (
              <Tooltip title={<IntlMessages id="leaveTypes.deleteLeaveType" />}>
                <DeleteOutlined
                  style={{
                    cursor: isFreemiumDisabledDelete ? 'not-allowed' : 'pointer',
                    opacity: isFreemiumDisabledDelete ? 0.5 : 1,
                  }}
                  onClick={() => (isFreemiumDisabledDelete ? null : handleMenuClick('deleteLeaveType', id, row.name))}
                />
              </Tooltip>
            )}
          </Space>
        )
      },
    },
  ]

  const handleCheckForLeaveTypeSimilarity = async (inputLeaveName: string) => {
    if (!inputLeaveName) {
      return
    }
    setIsCheckForLeaveTypeSimilarityLoading(true)
    try {
      const response = await Api.post('/core/check-leave-type-for-similarity', {
        leaveTypes: leaveTypesList.map((lt) => ({ id: lt.id, name: lt.name })),
        inputLeaveName,
      })
      setSimilarLeaveType(response)
      setIsCheckForLeaveTypeSimilarityLoading(false)
    } catch (error) {
      setIsCheckForLeaveTypeSimilarityLoading(false)
      Sentry.captureException(JSON.stringify(error))
    }
  }

  const handleCancel = () => {
    setIsCreateLeaveTypeAndAssignPolicyWizardVisible(false)
    setSelectedLeaveType(undefined)
    setSimilarLeaveType(null)
    setIsCheckForLeaveTypeSimilarityLoading(false)
  }

  return (
    <div className="main-content">
      <PageHeader
        title={<IntlMessages id="app.leaveTypes" />}
        actions={
          isLocationAdmin ? (
            <NoPermissionTooltip>
              <Button type="primary" size="large" disabled className="joyride-new-leave-type" data-qa="create-new-leave-type">
                <IntlMessages id="leaveTypes.createNewLeaveType" />
              </Button>
            </NoPermissionTooltip>
          ) : (
            <Button
              type="primary"
              size="large"
              onClick={createNewLeaveType}
              disabled={authCompany?.plan === SubscriptionPlanEnum.freemium}
              className="joyride-new-leave-type"
              data-qa="create-new-leave-type"
            >
              <IntlMessages id="leaveTypes.createNewLeaveType" />
            </Button>
          )
        }
      />
      <div className="main-content-body">
        <div className="leave-types-body">
          <Table
            loading={loading}
            dataSource={leaveTypesList}
            columns={leaveTypesColumns}
            rowKey="index"
            components={{
              body: {
                wrapper: DraggableContainer,
                row: DragableBodyRow,
              },
            }}
            pagination={false}
          />
          {isCreateLeaveTypeAndAssignPolicyWizardVisible && (
            <CreateLeaveTypeForm
              locations={locations}
              visibleModal={isCreateLeaveTypeAndAssignPolicyWizardVisible}
              handleCancel={handleCancel}
              setModalVisibility={setIsCreateLeaveTypeAndAssignPolicyWizardVisible}
              setAssigninPolicyCorrelationId={setAssigningPolicyCorrelationId}
              setShouldReloadOnActionNotificationsChange={setShouldReloadOnActionNotificationsChange}
              hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
              checkForLeaveTypeSimilarity={handleCheckForLeaveTypeSimilarity}
              similarLeaveType={similarLeaveType}
              isCheckForLeaveTypeSimilarityLoading={isCheckForLeaveTypeSimilarityLoading}
              resetSimilarLeaveType={() => setSimilarLeaveType(null)}
            />
          )}
          {isAssigningLeaveTypeToLocationModalVisible && (
            <CreateLeaveTypeForm
              locations={notAssignedLocations}
              visibleModal={isAssigningLeaveTypeToLocationModalVisible}
              handleCancel={() => {
                setIsAssigningLeaveTypeToLocationModalVisible(false)
                setNotAssignedLocations([])
              }}
              setModalVisibility={setIsAssigningLeaveTypeToLocationModalVisible}
              setAssigninPolicyCorrelationId={setAssigningPolicyCorrelationId}
              setShouldReloadOnActionNotificationsChange={setShouldReloadOnActionNotificationsChange}
              leaveType={{
                id: leaveTypeId,
                name: selectedLeaveTypeName,
              }}
              hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
            />
          )}
          {isUpdateLeaveTypeFormVisible && (
            <EditLeaveTypeForm
              visibleModal={isUpdateLeaveTypeFormVisible}
              handleCancel={() => {
                setUpdateLeaveTypeFormVisibility(false)
                setSelectedLeaveType(undefined)
              }}
              loading={updateLoading}
              leaveType={selectedLeaveType}
              onSave={onUpdateLeaveType}
              platform={platform}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default LeaveTypesPage
